/**
 * FileMakerStorageAdapter.ts - Storage adapter for FileMaker Data API
 * 
 * CHANGE HISTORY:
 * - 251031_1431 danielbeer: Initial creation for FileMaker integration
 */

import type { StorageAdapter, EntityData, QueryFilters } from '../types/AggregatrTypes';

/**
 * FileMaker connection configuration
 */
export interface FileMakerConfig {
  /** FileMaker Server URL */
  server: string;
  
  /** Database name */
  database: string;
  
  /** Layout name */
  layout: string;
  
  /** Authentication credentials */
  credentials: {
    username?: string;
    password?: string;
    token?: string;
    keystorePath?: string;
  };
  
  /** API version */
  apiVersion?: 'v1' | 'v2';
  
  /** Connection timeout (ms) */
  timeout?: number;
  
  /** Maximum retries */
  maxRetries?: number;
  
  /** Enable caching */
  enableCache?: boolean;
  
  /** Cache duration (ms) */
  cacheDuration?: number;
  
  /** Verify SSL certificates */
  verifySSL?: boolean;
  
  /** Debug logging */
  debug?: boolean;
}

/**
 * FileMaker field mapping
 */
export interface FileMakerFieldMapping {
  /** Entity field → FileMaker field */
  [entityField: string]: string;
}

/**
 * FileMaker Storage Adapter
 * Implements StorageAdapter interface for FileMaker Data API
 */
export class FileMakerStorageAdapter implements StorageAdapter {
  private config: FileMakerConfig;
  private fieldMapping: FileMakerFieldMapping;
  private token: string | null = null;
  private tokenExpiry: number = 0;
  private cache: Map<string, { data: any; expiry: number }>;

  constructor(config: FileMakerConfig, fieldMapping?: FileMakerFieldMapping) {
    this.config = {
      apiVersion: 'v1',
      timeout: 30000,
      maxRetries: 3,
      enableCache: true,
      cacheDuration: 300000, // 5 minutes
      verifySSL: true,
      debug: false,
      ...config
    };
    
    this.fieldMapping = fieldMapping || {};
    this.cache = new Map();
    
    if (this.config.debug) {
      console.log('[FileMakerStorageAdapter] Initialized:', {
        server: this.config.server,
        database: this.config.database,
        layout: this.config.layout
      });
    }
  }

  /**
   * Get authentication token
   */
  private async getToken(): Promise<string> {
    // Check if token is still valid
    if (this.token && Date.now() < this.tokenExpiry) {
      return this.token;
    }

    // Get credentials
    const credentials = await this.getCredentials();
    
    // Authenticate with FileMaker Data API
    const url = `${this.config.server}/fmi/data/${this.config.apiVersion}/databases/${this.config.database}/sessions`;
    
    try {
      const response = await this.fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
        }
      });

      const data = await response.json();
      
      if (data.messages[0].code !== '0') {
        throw new Error(`FileMaker authentication failed: ${data.messages[0].message}`);
      }

      this.token = data.response.token;
      // Token expires in 15 minutes, refresh after 14
      this.tokenExpiry = Date.now() + (14 * 60 * 1000);

      if (this.config.debug) {
        console.log('[FileMakerStorageAdapter] Authenticated successfully');
      }

      return this.token as string;
    } catch (error) {
      throw new Error(`FileMaker authentication failed: ${error}`);
    }
  }

  /**
   * Get credentials from config or keystore
   */
  private async getCredentials(): Promise<{ username: string; password: string }> {
    const { credentials } = this.config;
    
    // If credentials provided directly
    if (credentials.username && credentials.password) {
      return {
        username: credentials.username,
        password: credentials.password
      };
    }
    
    // If token provided
    if (credentials.token) {
      // TODO: Implement token-based auth
      throw new Error('Token-based auth not yet implemented');
    }
    
    // If keystore path provided
    if (credentials.keystorePath) {
      // TODO: Implement keystore integration
      throw new Error('Keystore integration not yet implemented');
    }
    
    throw new Error('No valid credentials provided');
  }

  /**
   * Fetch with retry logic
   */
  private async fetchWithRetry(url: string, options: RequestInit, retries = 0): Promise<Response> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok && retries < this.config.maxRetries!) {
        // Retry on server errors
        if (response.status >= 500) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retries + 1)));
          return this.fetchWithRetry(url, options, retries + 1);
        }
      }
      
      return response;
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw new Error('Request timeout');
      }
      
      if (retries < this.config.maxRetries!) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retries + 1)));
        return this.fetchWithRetry(url, options, retries + 1);
      }
      
      throw error;
    }
  }

  /**
   * Map entity data to FileMaker fields
   */
  private transformToFMData(data: any): Record<string, any> {
    const mapped: Record<string, any> = {};
    
    for (const [entityField, value] of Object.entries(data)) {
      const fmField = this.fieldMapping[entityField] || entityField;
      mapped[fmField] = value;
    }
    
    return mapped;
  }

  /**
   * Map FileMaker fields to entity data
   */
  private transformFromFMData(record: any): EntityData {
    const mapped: EntityData = {};
    
    // Reverse mapping
    const reverseMapping: Record<string, string> = {};
    for (const [entityField, fmField] of Object.entries(this.fieldMapping)) {
      reverseMapping[fmField] = entityField;
    }
    
    for (const [fmField, value] of Object.entries(record)) {
      const entityField = reverseMapping[fmField] || fmField;
      mapped[entityField] = value;
    }
    
    return mapped;
  }

  /**
   * Get cache key
   */
  private getCacheKey(type: string, id?: string): string {
    return id ? `${type}:${id}` : type;
  }

  /**
   * Get from cache
   */
  private getFromCache(key: string): any | null {
    if (!this.config.enableCache) return null;
    
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  /**
   * Set in cache
   */
  private setInCache(key: string, data: any): void {
    if (!this.config.enableCache) return;
    
    this.cache.set(key, {
      data,
      expiry: Date.now() + this.config.cacheDuration!
    });
  }

  /**
   * Clear cache
   */
  private clearCache(pattern?: string): void {
    if (pattern) {
      for (const key of this.cache.keys()) {
        if (key.startsWith(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get entity by ID
   */
  async get(type: string, id: string): Promise<EntityData | null> {
    // Check cache
    const cacheKey = this.getCacheKey(type, id);
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      if (this.config.debug) {
        console.log('[FileMakerStorageAdapter] Cache hit:', cacheKey);
      }
      return cached;
    }

    const token = await this.getToken();
    const url = `${this.config.server}/fmi/data/${this.config.apiVersion}/databases/${this.config.database}/layouts/${this.config.layout}/records/${id}`;
    
    try {
      const response = await this.fetchWithRetry(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.messages[0].code !== '0') {
        if (data.messages[0].code === '101') {
          // Record not found
          return null;
        }
        throw new Error(`FileMaker error: ${data.messages[0].message}`);
      }

      const record = this.transformFromFMData(data.response.data[0].fieldData);
      record.id = id;
      record.type = type;
      
      // Cache result
      this.setInCache(cacheKey, record);
      
      return record;
    } catch (error) {
      console.error('[FileMakerStorageAdapter] Get error:', error);
      throw error;
    }
  }

  /**
   * Set entity
   */
  async set(type: string, id: string, data: EntityData): Promise<EntityData> {
    const token = await this.getToken();
    const fmData = this.transformToFMData(data);
    
    // Check if record exists
    const existing = await this.get(type, id);
    const url = existing
      ? `${this.config.server}/fmi/data/${this.config.apiVersion}/databases/${this.config.database}/layouts/${this.config.layout}/records/${id}`
      : `${this.config.server}/fmi/data/${this.config.apiVersion}/databases/${this.config.database}/layouts/${this.config.layout}/records`;
    
    try {
      const response = await this.fetchWithRetry(url, {
        method: existing ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ fieldData: fmData })
      });

      const result = await response.json();
      
      if (result.messages[0].code !== '0') {
        throw new Error(`FileMaker error: ${result.messages[0].message}`);
      }

      // Clear cache for this type
      this.clearCache(type);
      
      if (this.config.debug) {
        console.log('[FileMakerStorageAdapter] Set successful:', { type, id });
      }
      
      // Return the stored entity
      const stored = { ...data, id, type };
      return stored;
    } catch (error) {
      console.error('[FileMakerStorageAdapter] Set error:', error);
      throw error;
    }
  }

  /**
   * Delete entity
   */
  async delete(type: string, id: string): Promise<boolean> {
    const token = await this.getToken();
    const url = `${this.config.server}/fmi/data/${this.config.apiVersion}/databases/${this.config.database}/layouts/${this.config.layout}/records/${id}`;
    
    try {
      const response = await this.fetchWithRetry(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.messages[0].code !== '0') {
        if (data.messages[0].code === '101') {
          // Record not found
          return false;
        }
        throw new Error(`FileMaker error: ${data.messages[0].message}`);
      }

      // Clear cache
      this.clearCache(type);
      
      if (this.config.debug) {
        console.log('[FileMakerStorageAdapter] Delete successful:', { type, id });
      }
      
      return true;
    } catch (error) {
      console.error('[FileMakerStorageAdapter] Delete error:', error);
      throw error;
    }
  }

  /**
   * Query entities
   */
  async query(type: string, filters?: QueryFilters): Promise<EntityData[]> {
    // Check cache for simple queries
    if (!filters || Object.keys(filters).length === 0) {
      const cacheKey = this.getCacheKey(type);
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        if (this.config.debug) {
          console.log('[FileMakerStorageAdapter] Cache hit:', cacheKey);
        }
        return cached;
      }
    }

    const token = await this.getToken();
    const url = `${this.config.server}/fmi/data/${this.config.apiVersion}/databases/${this.config.database}/layouts/${this.config.layout}/_find`;
    
    // Build FileMaker query
    const fmQuery = this.buildFileMakerQuery(filters);
    
    try {
      const response = await this.fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(fmQuery)
      });

      const data = await response.json();
      
      if (data.messages[0].code !== '0') {
        if (data.messages[0].code === '401') {
          // No records found
          return [];
        }
        throw new Error(`FileMaker error: ${data.messages[0].message}`);
      }

      const records = data.response.data.map((record: any) => {
        const mapped = this.transformFromFMData(record.fieldData);
        mapped.id = record.recordId;
        mapped.type = type;
        return mapped;
      });
      
      // Cache results for simple queries
      if (!filters || Object.keys(filters).length === 0) {
        this.setInCache(this.getCacheKey(type), records);
      }
      
      return records;
    } catch (error) {
      console.error('[FileMakerStorageAdapter] Query error:', error);
      throw error;
    }
  }

  /**
   * Build FileMaker query from filters
   */
  private buildFileMakerQuery(filters?: QueryFilters): any {
    if (!filters || Object.keys(filters).length === 0) {
      return {
        query: [{}],
        limit: 100
      };
    }

    const query: any[] = [];
    const queryItem: Record<string, any> = {};

    for (const [field, value] of Object.entries(filters)) {
      const fmField = this.fieldMapping[field] || field;
      
      if (typeof value === 'object' && value !== null) {
        // Handle operators
        if ('$eq' in value) {
          queryItem[fmField] = value.$eq;
        } else if ('$ne' in value) {
          queryItem[fmField] = `!${value.$ne}`;
        } else if ('$gt' in value) {
          queryItem[fmField] = `>${value.$gt}`;
        } else if ('$gte' in value) {
          queryItem[fmField] = `>=${value.$gte}`;
        } else if ('$lt' in value) {
          queryItem[fmField] = `<${value.$lt}`;
        } else if ('$lte' in value) {
          queryItem[fmField] = `<=${value.$lte}`;
        } else if ('$contains' in value) {
          queryItem[fmField] = `*${value.$contains}*`;
        }
      } else {
        // Direct value
        queryItem[fmField] = value;
      }
    }

    query.push(queryItem);

    return {
      query,
      limit: 100
    };
  }

  /**
   * Close connection and cleanup
   */
  async close(): Promise<void> {
    if (this.token) {
      try {
        const url = `${this.config.server}/fmi/data/${this.config.apiVersion}/databases/${this.config.database}/sessions/${this.token}`;
        await this.fetchWithRetry(url, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (this.config.debug) {
          console.log('[FileMakerStorageAdapter] Session closed');
        }
      } catch (error) {
        console.error('[FileMakerStorageAdapter] Error closing session:', error);
      }
      
      this.token = null;
      this.tokenExpiry = 0;
    }
    
    this.cache.clear();
  }

  /**
   * Test connection
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.getToken();
      return true;
    } catch (error) {
      console.error('[FileMakerStorageAdapter] Connection test failed:', error);
      return false;
    }
  }
}

export default FileMakerStorageAdapter;
